import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
//import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { MatSort } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ActivatedRoute, Router, RouterLinkWithHref } from "@angular/router";
import {
  MatDialogModule,
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogConfig,
} from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { MsalService } from "@azure/msal-angular";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { ListSectionsService } from "../../../data/section/list-sections.service";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";

import dayjs from "dayjs";

// For Dialog view
import {
  SectionDetailsComponent,
  SectionData,
} from "../section-details/section-details.component";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TableSkeletonComponent } from "../../../shared/skeleton";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { SECTIONS_LIST_I18N } from "./sections-list.i18n";

@Component({
  selector: "app-sections-list",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./sections-list.component.html",
  styleUrls: ["./sections-list.component.scss"],
  imports: [...SHARED_IMPORTS, TableSkeletonComponent, PageHeaderComponent],
})
export class SectionsListComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = SECTIONS_LIST_I18N;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("menuTrigger") menuTrigger: MatMenuTrigger;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  loginUserId = "";
  filter = ""; //table filtering
  isLoading = signal(true);
  readonly searchExpanded = signal(false);

  sectionGroupIdInUrl;
  hasSectionManagement;
  hasAllSectionGroupManagement;
  selectedSectionGroup: any;
  currentUrl = "";
  userType = "";
  userTypeDisp = "";

  sections: SectionData[] = [];
  displayedColumns = ["sectionName", "description", "address", "status", "admins"];

  accountStatus: { [key: string]: string } = {
    OPEN: this.i18n.status.active,
    CLOSE: this.i18n.status.inactive,
  };

  //dialog with path param
  private dialogRef: MatDialogRef<any, any>;
  private routerUrl: string;

  dataSource: MatTableDataSource<SectionData>;

  constructor(
    public storeUser: StoreUserService,
    private route: ActivatedRoute,
    private listSectionsService: ListSectionsService,
    private router: Router,
    private msalService: MsalService,
    public dialog: MatDialog,
    private domSanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry,
    private common: DatabaseCacheService,
  ) {
    let user = this.msalService.instance.getActiveAccount();
    this.loginUserId = user.idTokenClaims["sub"];

    // カスタムSVG iconの呼び出し
    this.iconRegistry.addSvgIcon(
      "icon_staff", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_staff.svg",
      ), //必ずサニタイザーを利用して取得する。
    );

    this.iconRegistry.addSvgIcon(
      "icon_setting", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_setting.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-close", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-close.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-open", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-open.svg",
      ),
    );
  }

  ngOnInit() {
    const userType = this.common.getUserType(this.router.url);

    this.userType = userType.userType;
    this.userTypeDisp = userType.userTypeDisp;

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.sectionGroupIdInUrl = params.get("sectionGroupId"); // sectionGroupId in the URL pass
      this.listSections(this.sectionGroupIdInUrl);

      this.hasSectionManagement = this.common.hasSectionManagement(
        this.sectionGroupIdInUrl,
      );
      this.hasAllSectionGroupManagement =
        this.common.hasAllSectionGroupManagement();

      this.selectedSectionGroup = this.common
        .getSectionGroupDocuments()
        ?.find((s) => s.id == this.sectionGroupIdInUrl);
    });

    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {
      // Sectionに関わるデータの読み込み
      this.selectedSectionGroup = this.common
        .getSectionGroupDocuments()
        ?.find((s) => s.id == this.sectionGroupIdInUrl);
    });
  }

  ngAfterViewInit(): void {}

  applyFilter(event?: Event) {
    if (event) {
      this.filter = (event.target as HTMLInputElement).value;
    }
    const filterValue = this.filter.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // M3 Expandable Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filter.trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filter = "";
    this.applyFilter();
  }

  refreshData(): void {
    this.listSections(this.sectionGroupIdInUrl);
  }

  listSections(sectionGroupId) {
    this.isLoading.set(true);
    this.listSectionsService.listSections(sectionGroupId, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res) => {
        var resArray = res as Array<any>;

        this.sections = [];
        for (let i = 0; i < resArray.length; i++) {
          var section = resArray[i];

          var sectionData: SectionData = {
            sectionGroupId: section.sectionGroupId,

            id: section.id,
            sectionName: section.sectionName,
            description: section.description,

            numOfLicence: section.numOfLicence,

            zip: section.addresses.zip,
            prefecture: section.addresses.prefecture,
            city: section.addresses.city,
            town: section.addresses.town,
            line1: section.addresses.line1,
            line2: section.addresses.line2,

            phone: section.contactDetails.phone,
            mobile: section.contactDetails.mobile,
            email: section.contactDetails.email,

            contractStartAt: dayjs(
              section.contractInfo.contractStartAt,
            ).isValid()
              ? dayjs(section.contractInfo.contractStartAt).format("YYYY/MM/DD")
              : null,
            contractEndAt: dayjs(section.contractInfo.contractEndAt).isValid()
              ? dayjs(section.contractInfo.contractEndAt).format("YYYY/MM/DD")
              : null,
            contractStatus: section.contractInfo.contractStatus,

            quotaModel: section.settings?.quotaModel,
            monthlyQuotaGB: section.settings?.monthlyQuotaGB,

            admins: section.primaryStaffs,
          };

          this.sections.push(sectionData);
        }

        this.sections.sort((a, b) => {
          const statusA = a.contractStatus || '';
          const statusB = b.contractStatus || '';

          if (statusA < statusB) return 1;
          if (statusA > statusB) return -1;
          return 0; // 同一の場合は順序を維持（最近のブラウザのsortは安定ソートです）
        });
        this.dataSource = new MatTableDataSource(this.sections);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.isLoading.set(false);
      },
      (error) => {
        this.isLoading.set(false);
      },
    );
  }

  openCreateSectionDialog() {
    const newSection = {
      sectionGroupId: this.sectionGroupIdInUrl,
    };
    this.openSectionDetailDialog(newSection);
  }

  openSectionDetailDialog(section) {
    const dialogRef = this.dialog.open(SectionDetailsComponent, {
      width: "740px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        section: section,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (result) => {
      if (result?.needRefresh) {
        await this.common.refreshAllData();
        this.listSections(this.sectionGroupIdInUrl);
      }
    });
  }
}
